import React, { useState } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useLanguage } from '@/context/LanguageContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, X, GripVertical, Edit } from 'lucide-react'
import { UPDATE_PRODUCT_FEATURES, GET_PRODUCT_DETAILS } from '../productQueries'
import { GET_PRODUCT_FEATURES_FOR_SELECT } from '../../ProductFeatures/productFeaturesQueries'

interface ProductFeature {
  id: number
  display_order: number
  product_feature: {
    id: number
    photo?: {
      id: number
      src_xs: string
      src: string
    }
    product_feature_profiles: {
      id: number
      name: string
      description: string
      language: string
    }[]
  }
}

interface AvailableFeature {
  id: number
  photo?: {
    id: number
    src_xs: string
    src: string
  }
  product_feature_profiles: {
    id: number
    name: string
    description?: string
    language: string
  }[]
  product_containers: {
    id: number
  }[]
}

interface ProductFeaturesCardProps {
  productId: number
  assignedFeatures: ProductFeature[]
  language: string
}

export function ProductFeaturesCard({
  productId,
  assignedFeatures,
  language,
}: ProductFeaturesCardProps) {
  const { language: currentLanguage } = useLanguage()
  const [isManaging, setIsManaging] = useState(false)
  const [localFeatures, setLocalFeatures] =
    useState<ProductFeature[]>(assignedFeatures)

  const [
    loadAvailableFeatures,
    { data: availableFeaturesData, loading: loadingAvailableFeatures },
  ] = useLazyQuery(GET_PRODUCT_FEATURES_FOR_SELECT, {
    fetchPolicy: 'cache-and-network',
  })

  const [updateProductFeatures, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_FEATURES,
    {
      refetchQueries: [
        { query: GET_PRODUCT_DETAILS, variables: { id: productId } },
      ],
      onCompleted: () => {
        setIsManaging(false)
      },
    }
  )

  const availableFeatures: AvailableFeature[] =
    availableFeaturesData?.findManyProductFeature || []

  // Get assigned feature IDs for easy lookup
  const assignedFeatureIds = new Set(
    localFeatures.map(f => f.product_feature.id)
  )

  // Filter available features to show only unassigned ones
  const unassignedFeatures = availableFeatures.filter(
    feature => !assignedFeatureIds.has(feature.id)
  )

  const getFeatureName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(
        profile =>
          profile.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      'Unknown Feature'
    )
  }

  const getFeatureDescription = (
    profiles: { name: string; description: string; language: string }[]
  ) => {
    return (
      profiles.find(
        profile =>
          profile.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.description ||
      profiles[0]?.description ||
      ''
    )
  }

  const handleAddFeature = (featureId: number) => {
    const featureToAdd = availableFeatures.find(f => f.id === featureId)
    if (!featureToAdd) return

    const newFeature: ProductFeature = {
      id: Date.now(), // Temporary ID for local state
      display_order: localFeatures.length + 1,
      product_feature: {
        id: featureToAdd.id,
        photo: featureToAdd.photo,
        product_feature_profiles: featureToAdd.product_feature_profiles.map(
          profile => ({
            ...profile,
            description: profile.description || '',
          })
        ),
      },
    }

    setLocalFeatures([...localFeatures, newFeature])
  }

  const handleRemoveFeature = (featureId: number) => {
    setLocalFeatures(
      localFeatures.filter(f => f.product_feature.id !== featureId)
    )
  }

  const handleMoveFeature = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === localFeatures.length - 1)
    ) {
      return
    }

    const newFeatures = [...localFeatures]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    // Swap elements
    const temp = newFeatures[index]
    newFeatures[index] = newFeatures[targetIndex]
    newFeatures[targetIndex] = temp

    // Update display_order
    newFeatures.forEach((feature, i) => {
      feature.display_order = i + 1
    })

    setLocalFeatures(newFeatures)
  }

  const handleSave = async () => {
    try {
      await updateProductFeatures({
        variables: {
          id: productId,
          product_features: localFeatures.map((feature, index) => ({
            id: feature.product_feature.id,
            display_order: index + 1,
          })),
        },
      })
    } catch (error) {
      console.error('Error updating product features:', error)
    }
  }

  const handleCancel = () => {
    setLocalFeatures(assignedFeatures)
    setIsManaging(false)
  }

  const hasChanges =
    JSON.stringify(
      localFeatures.map(f => ({
        id: f.product_feature.id,
        order: f.display_order,
      }))
    ) !==
    JSON.stringify(
      assignedFeatures.map(f => ({
        id: f.product_feature.id,
        order: f.display_order,
      }))
    )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Product Features</CardTitle>
        <div className="flex space-x-2">
          {!isManaging && (
            <Button
              onClick={() => {
                setIsManaging(true)
                loadAvailableFeatures()
              }}
              size="sm"
              variant="ghost"
            >
              <Edit className="h-4 w-4 mr-2" />
            </Button>
          )}
          {isManaging && (
            <>
              <Button
                onClick={handleCancel}
                size="sm"
                variant="ghost"
                disabled={updating}
              >
                <X className="h-4 w-4 text-red-500" />
                Cancel
              </Button>
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  size="sm"
                  variant="default"
                  disabled={updating}
                >
                  Save Changes
                </Button>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* Assigned Features */}
          {localFeatures.length > 0 ? (
            <div>
              {isManaging && (
                <div className="mb-3 pt-3 border-t text-xs">
                  Assigned Features
                </div>
              )}
              <div className="space-y-2">
                {localFeatures.map((feature, index) => (
                  <div
                    key={feature.product_feature.id}
                    className="flex items-center justify-between p-2 "
                  >
                    <div className="flex items-center space-x-3">
                      {feature.product_feature.photo && (
                        <img
                          src={
                            feature.product_feature.photo.src_xs ??
                            feature.product_feature.photo.src
                          }
                          alt={getFeatureName(
                            feature.product_feature.product_feature_profiles
                          )}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {getFeatureName(
                            feature.product_feature.product_feature_profiles
                          )}
                        </p>
                        {getFeatureDescription(
                          feature.product_feature.product_feature_profiles
                        ) && (
                          <p className="text-xs text-gray-600">
                            {getFeatureDescription(
                              feature.product_feature.product_feature_profiles
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    {isManaging && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          handleRemoveFeature(feature.product_feature.id)
                        }
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !isManaging && (
              <p className="text-gray-500 text-sm">
                No features assigned to this product.
              </p>
            )
          )}

          {/* Available Features (only show when managing) */}
          {isManaging && (
            <div>
              <div className="mb-3 pt-3 border-t text-xs">
                Available Features
              </div>
              {loadingAvailableFeatures ? (
                <div className="flex justify-center py-4">
                  <div className="text-sm text-gray-500">
                    Loading available features...
                  </div>
                </div>
              ) : unassignedFeatures.length > 0 ? (
                <div className="space-y-2">
                  {unassignedFeatures.map(feature => (
                    <div
                      key={feature.id}
                      className="flex items-center justify-between p-2 border rounded-lg bg-white"
                    >
                      <div className="flex items-center space-x-3 text-sm">
                        {feature.photo && (
                          <img
                            src={feature.photo.src_xs ?? feature.photo.src}
                            alt={getFeatureName(
                              feature.product_feature_profiles
                            )}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <span>
                          {getFeatureName(feature.product_feature_profiles)}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddFeature(feature.id)}
                      >
                        <Plus className="h-4 w-4 text-green-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : localFeatures.length > 0 ? (
                <p className="text-gray-500 text-xs">
                  All available features are already assigned to this product.
                </p>
              ) : (
                <p className="text-gray-500 text-xs">
                  No features available to assign.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
